import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import { FormField } from "@avamae/formbuilder";
import {
  FormikErrors,
  useField,
  useFormikContext,
} from "formik";
import { Dropdown, SelectOption } from "components/dropdown/Dropdown";
import { FormFieldTooltip } from "components/helpers/FormFieldTooltip/FormFieldTooltip";
import { handleAddressSearch } from "./Geocoding/Geocoding";
import Map, { MapData } from "./Map";
import { pascalCaseToCamelCase } from "helpers/pascalCaseToCamelCase";
import errorIcon from "assets/Icon_Notification_Warning.png";
import { clearMapData, useUpdateCoordinates } from "./Geocoding/helper";
import {
  AddressMapData,
  GeoCodingData,
  searchParams,
} from "./Geocoding/models";
import { toast } from "react-toastify";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { SiteFields } from "pages/Sites/models";

const tooltipMainIconSource = require("assets/Tooltip.png");
const tooltipCloseIconSource = require("assets/Icon_Btn_06.png");

const createParsedAddress = (field: any) => {
  let addressSummary: string[] = [];
  if (field.value?.addressLine1) {
    addressSummary = [...addressSummary, field.value.addressLine1];
  }
  if (field.value?.addressLine2) {
    addressSummary = [...addressSummary, field.value.addressLine2];
  }
  if (field.value?.addressLine3) {
    addressSummary = [...addressSummary, field.value.addressLine3];
  }
  if (field.value?.addressLine4) {
    addressSummary = [...addressSummary, field.value.addressLine4];
  }
  if (field.value?.city) {
    addressSummary = [...addressSummary, field.value.city];
  }
  if (field.value?.stateProvince) {
    addressSummary = [...addressSummary, field.value.stateProvince];
  }
  if (field.value?.usStatesID) {
    const stateMatch = field.value.usStates?.filter(
      (usState: SelectOption) => usState.value === field.value.usStatesID
    )?.[0];
    if (stateMatch) {
      addressSummary = [...addressSummary, stateMatch.label];
    }
  }
  if (field.value?.countriesId) {
    const countryMatch = field.value.countries?.filter(
      (country: SelectOption) => country.value === field.value.countriesId
    )?.[0];
    if (countryMatch) {
      addressSummary = [...addressSummary, countryMatch.label];
    }
  }
  if (field.value?.postalZipCode) {
    addressSummary = [...addressSummary, field.value.postalZipCode];
  }
  if (field.value?.latitude) {
    addressSummary = [...addressSummary, field.value.latitude];
  }
  if (field.value?.longitude) {
    addressSummary = [...addressSummary, field.value.longitude];
  }

  return addressSummary.join(", ").trim();
};

type MapCreateEditProps = {
  name: string;
  dataSource: string | null;
  label: string;
  tooltip: string | null;
  disableLongitudeAndLatitude?: boolean;
};

type MapEndpointData = {
  addressLine1: string | null;
  city: string | null;
  stateProvince: string | null;
  country: SelectOption | null;
  addressLine2: string | null;
  postalZipCode: string | null;
  latitude: string | null;
  longitude: string | null;
};

const MapCreateEdit: React.FC<MapCreateEditProps> = ({
  name,
  dataSource,
  label,
  tooltip,
  disableLongitudeAndLatitude,
}) => {
  const findAddressRef = useRef(0);
  const [editPanel, setEditPanel] = useState<boolean>(false);
  const [mapData, setMapData] = useState<MapEndpointData | null>(null);
  const [relocateSubmitCount, setRelocateSubmitCount] = useState<number>(0);
  const [countries, setCountries] = useState<SelectOption[] | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<SelectOption | null>(
    null
  );
  const [usStates, setUsStates] = useState<SelectOption[] | null>(null);
  const [selectedUsState, setSelectedUsState] = useState<SelectOption | null>(
    null
  );

  const [findingAddress, setFindingAddress] = useState<boolean>(false);
  const [update, setUpdate] = useState<"RELOCATE" | "EDIT" | null>(null);
  const [GeoCodingError, setGeoCodingError] = useState<boolean>(false);
  const [allAddressFieldsPopulated, setAllAddressFieldsPopulated] = useState<
    boolean
  >(false);
  const [parsedAddressSummary, setParsedAddressSummary] = useState<
    string | null
  >(null);
  const [relevantFormikError, setRelevantFormikError] = useState<any>(null);
  const [fieldValidateToggle, setFieldValidateToggle] = useState<boolean>(
    false
  );
  const [fieldsTouched, setFieldsTouched] = useState<boolean>(false);
  const [coordinates, setCoordinates] = useState<MapData[] | null>(null);
  const [findAddressClickedCount, setFindAddressClickedCount] = useState<
    number
  >(0);
  const [showFindAddressAlert, setShowFindAddressAlert] = useState<boolean>(
    false
  );

  const [existingCoordinatesSet, setExistingCoordinatesSet] = useState<boolean>(
    false
  );

  const { values } = useFormikContext<any>();
  const [
    previousAddressMemo,
    setPreviousAddressMemo,
  ] = useState<AddressMapData | null>(null);

  const [field] = useField<any>(name);
  // const dispatch = useDispatch();
  const {
    errors,
    setFieldValue,
    submitCount,
    validateField,
    touched,
    setFieldTouched,
    setFieldError,
  } = useFormikContext<{}>();

  const updateRelevantFormikErrors = () => {
    if (errors.hasOwnProperty(field.name)) {
      const error = errors[field.name as keyof typeof errors];

      if (typeof error === "object") {
        const errs = errors[field.name as keyof typeof errors];

        setRelevantFormikError(errs);
        return true;
      }
    } else {
      setRelevantFormikError(null);
    }

    return false;
  };
  useEffect(() => {
    if (
      values.id &&
      field.value &&
      field.value.hasOwnProperty("longitude") &&
      field.value.hasOwnProperty("latitude") &&
      !field.value.longitude &&
      !field.value.latitude
    ) {
      const parsedAddress = createParsedAddress(field);
      const queryObj: searchParams = { address: parsedAddress };
      (async () => {
        const geoCodingResponse = await handleAddressSearch(
          field,
          queryObj,
          field.value
        );
        if (
          geoCodingResponse &&
          typeof geoCodingResponse === "object" &&
          geoCodingResponse.hasOwnProperty("mapData") &&
          geoCodingResponse.mapData.hasOwnProperty("longitude") &&
          geoCodingResponse.mapData.hasOwnProperty("latitude") &&
          geoCodingResponse.mapData.latitude !== null &&
          typeof geoCodingResponse.mapData.latitude === "number" &&
          geoCodingResponse.mapData.longitude !== null &&
          typeof geoCodingResponse.mapData.longitude === "number"
        ) {
          setFieldValue(name, geoCodingResponse.mapData);
        }
      })();
    }
  }, []);

  //set revalent states if in create panel
  useEffect(() => {
    if (values.id) {
      setEditPanel(true);
      setFindingAddress(true);
      setFieldValue("addressValid", true);
    }
  }, []);
  //**FORMIK */
  //Update formik touched
  useEffect(() => {
    if (touched && Object.keys(touched).length > 0) {
      setFieldsTouched(true);
    }
  }, [touched]);
  //Update formik errors
  useEffect(() => {
    const errorsExist = updateRelevantFormikErrors();

    if (
      !errorsExist &&
      fieldsTouched &&
      (findAddressClickedCount > 0 || submitCount > 0)
    ) {
      setAllAddressFieldsPopulated(true);
    } else {
      setAllAddressFieldsPopulated(false);
    }
  }, [fieldValidateToggle]);
  //formik on blur
  const handleOnBlur = (fieldName: string) => {
    setFieldTouched(fieldName, true);
    validate();
  };
  //**INPUT CHANGE */
  //Set initial country dropdown options and selected country id on load
  useEffect(() => {
    if (field.value) {
      const existingValues = field.value;

      let tempCountries = null;

      if (field.value.hasOwnProperty("countries")) {
        tempCountries = field.value["countries" as keyof typeof field.value];

        setCountries(tempCountries);

        if (
          tempCountries &&
          Array.isArray(tempCountries) &&
          tempCountries.length > 0 &&
          tempCountries[0] &&
          typeof tempCountries[0] === "object" &&
          tempCountries[0].hasOwnProperty("value") &&
          existingValues &&
          typeof existingValues === "object" &&
          existingValues.hasOwnProperty("countriesId")
        ) {
          const matchingCountry = tempCountries.filter(
            item =>
              item.value ===
              existingValues["countriesId" as keyof typeof existingValues]
          );

          if (
            matchingCountry &&
            matchingCountry.length === 1 &&
            selectedCountry !== matchingCountry[0]
          ) {
            setSelectedCountry(matchingCountry[0]);
          }
        }
      }
      setMapData(existingValues);
    }
  }, [field.value]);

  const [countriesInfo, setCountriesInfo] = useState<{
    [id: number]: string;
  } | null>(null);

  //Set initla US State dropdown options and selected state on load
  useEffect(() => {
    if (field.value) {
      const existingValues = field.value;

      let tempUsStates = null;

      if (field.value.hasOwnProperty("usStates")) {
        tempUsStates = field.value["usStates" as keyof typeof field.value];
        setUsStates(tempUsStates);

        if (
          tempUsStates &&
          Array.isArray(tempUsStates) &&
          tempUsStates.length > 0 &&
          tempUsStates[0] &&
          typeof tempUsStates[0] === "object" &&
          tempUsStates[0].hasOwnProperty("value") &&
          existingValues &&
          typeof existingValues === "object" &&
          existingValues.hasOwnProperty("usStatesID")
        ) {
          const matchingUsState = tempUsStates.filter(
            item =>
              item.value ===
              existingValues["usStatesID" as keyof typeof existingValues]
          );

          if (
            matchingUsState &&
            matchingUsState.length === 1 &&
            selectedUsState !== matchingUsState[0]
          ) {
            setSelectedUsState(matchingUsState[0]);
          }
        }
      }

      setMapData(existingValues);

      if (
        Array.isArray(field.value["countries"]) &&
        field.value["countries"].length > 0 &&
        typeof field.value["countries"][0] === "object"
      ) {
        let countriesData: any = {};

        field.value["countries"].forEach(item => {
          if (
            item.hasOwnProperty("internalCode") &&
            typeof item.internalCode === "string" &&
            item.hasOwnProperty("value")
          ) {
            countriesData[item.value] = item.internalCode;
          }
        });

        if (countriesInfo !== countriesData) {
          setCountriesInfo(countriesData);
        }
      } else if (countriesInfo !== null) {
        setCountriesInfo(null);
      }
    }
  }, [field.value]);
  //clear US states if country changed from USA
  useEffect(() => {
    if (
      field.value &&
      field.value.hasOwnProperty("countriesId") &&
      countriesInfo
    ) {
      const newMapData = { ...field.value };

      const countryId = newMapData["countriesId"];

      if (
        countryId &&
        countriesInfo &&
        countriesInfo.hasOwnProperty(countryId) &&
        countriesInfo[countryId] === "UNITED_STATES_OF_AMERICA"
        // countriesInfo[countryId] === "united states of america"
      ) {
        // if (newMapData["countriesId"] === 1001) {
        newMapData["stateProvince"] = null;
      } else {
        newMapData["usStatesID"] = null;
        setSelectedUsState(null);
      }

      setFieldValue(name, newMapData);
    }
  }, [field.value["countriesId"]]);
  //input on change function
  const onChange = (mapFieldName: string, newValue: string) => {
    let newMapData: any = { ...field.value };

    if (
      !disableLongitudeAndLatitude &&
      (mapFieldName === "latitude" || mapFieldName === "longitude") &&
      typeof newValue === "string" &&
      newValue !== ""
    ) {
      newMapData[mapFieldName] = parseFloat(newValue);
    } else if (
      !disableLongitudeAndLatitude &&
      (mapFieldName === "latitude" || mapFieldName === "longitude") &&
      newValue === ""
    ) {
      newMapData[mapFieldName] = null;
    } else {
      newMapData[mapFieldName] = newValue;
    }
    setFieldValue(name, newMapData);
    //}
  };
  //update field on country select
  const countryOnChange = (selectedOption: SelectOption) => {
    if (field.value.hasOwnProperty("countriesId")) {
      let newMapData: any = { ...field.value };

      newMapData["countriesId"] = selectedOption.value;

      if (selectedOption.hasOwnProperty("internalCode")) {
        const internalCode =
          selectedOption["internalCode" as keyof typeof selectedOption];

        if (
          internalCode === "UNITED_STATES_OF_AMERICA" &&
          field.value["stateProvince"]
        ) {
          newMapData["stateProvince"] = "";
        } else if (internalCode === "UNITES_KINGDOM") {
          newMapData["usStatesID"] = null;

          setSelectedUsState(null);
        }
      }

      setFieldValue(name, newMapData);

      setSelectedCountry(selectedOption);
    }
  };
  //update field on US State select
  const stateOnChange = (selectedOption: SelectOption) => {
    if (field.value.hasOwnProperty("usStatesID")) {
      let newMapData: any = { ...field.value };
      newMapData["usStatesID"] = selectedOption.value;
      setFieldValue(name, newMapData);

      setSelectedUsState(selectedOption);
    }
  };
  //**RELOCATE/EDIT */
  //set relevant states for relocate 'mode'
  const relocateSite = () => {
    //clear form data
    let newFieldValue = clearMapData(field);
    //save original form data if user cancels relocate
    setPreviousAddressMemo({ ...field.value });

    setSelectedUsState(null);
    setSelectedCountry(null);
    setFieldValue(field.name, newFieldValue);
    //set
    setUpdate("RELOCATE");
    setFindingAddress(false);
    // setFieldValue("addressValid", null);
  };
  //set relevant states if relocate is cancelled in the edit panel
  const cancelRelocate = () => {
    if (update === "RELOCATE") {
      setFieldValue(name, previousAddressMemo);
      setParsedAddressSummary(
        createParsedAddress({ value: { ...previousAddressMemo } })
      );
    }
    setUpdate(null);
    setFindingAddress(true);
    setFieldValue("addressValid", true);
    setRelocateSubmitCount(1);
  };
  //If user wishes to edit address after find address has been used:
  // clear long and lat to remove map and reset find address state
  const editSite = () => {
    // setFieldValue(name, { ...field.value, longitude: null, latitude: null });
    setFindingAddress(false);
    setUpdate("EDIT");
  };

  //**TOAST MESSAGE IF USER ATTEMPTS SUBMIT WIHTOUT VALIDATING DETAILS WITH FIND ADDRESS

  //If in edit panel track submit attempts whilst in relocate 'mode'
  useEffect(() => {
    if (editPanel && update) {
      setRelocateSubmitCount(prevValue => prevValue++);
    }
  }, [submitCount, update]);
  //action toast message if 1st submit attempt made. clear error so that if user wishes they may submit on second attempt without validating
  useEffect(() => {
    if (
      (errors["addressValid" as keyof FormikErrors<{}>] &&
        submitCount === 1 &&
        !editPanel) ||
      (errors["addressValid" as keyof FormikErrors<{}>] &&
        relocateSubmitCount === 1 &&
        editPanel)
    ) {
      toast(
        <ToastMessage
          toastData={[
            {
              title: "Validate",
              text: `Please use the find address button to validate before adding the site. If you do not wish to validate please continue using the ${
                editPanel ? "Update" : "Add"
              } button`,
            },
          ]}
          className="ToastWarning"
        />,
        {
          ...toastConfig,
        }
      );
      setFieldValue("addressValid", true);
    }
  }, [submitCount]);
  //set coordinates
  useEffect(() => {
    {
      if (
        !disableLongitudeAndLatitude &&
        field.value &&
        field.value.hasOwnProperty("latitude") &&
        field.value["latitude" as keyof typeof field.value] !== null &&
        typeof field.value["latitude" as keyof typeof field.value] ===
          "number" &&
        field.value.hasOwnProperty("longitude") &&
        field.value["longitude" as keyof typeof field.value] !== null &&
        typeof field.value["longitude" as keyof typeof field.value] === "number"
      ) {
        const tempCoordinates = [
          {
            location: {
              lat: field.value["latitude" as keyof typeof field.value],
              lng: field.value["longitude" as keyof typeof field.value],
            },
            label: undefined,
            id: 1,
          },
        ];
        setCoordinates(tempCoordinates);

        if (!existingCoordinatesSet) {
          findAddressCheck(tempCoordinates);
        }
      } else {
        setCoordinates(null);
      }

      setExistingCoordinatesSet(true);
    }
  }, [field.value]);

  const validate = () => {
    validateField(field.name);

    setFieldValidateToggle(!fieldValidateToggle);
  };

  //function to handle the Google api response
  const handleGeoCodingResponse = (
    geoCodingResponse: string | GeoCodingData | null
  ) => {
    if (geoCodingResponse && typeof geoCodingResponse === "object") {
      setFieldValue(name, {
        ...field.value,
        latitude: geoCodingResponse.mapData.latitude,
        longitude: geoCodingResponse.mapData.longitude,
        addressLine1:
          field.value.addressLine1 ?? geoCodingResponse.mapData.address,
        addressLine2:
          field.value.addressLine2 ?? geoCodingResponse.mapData.region,
        city: field.value.city ?? geoCodingResponse.mapData.city,
        stateProvince:
          field.value.stateProvince ?? geoCodingResponse.mapData.stateProvince,
      });
      setFieldValue("addressValid", true);
      if (
        geoCodingResponse.mapData.latitude &&
        geoCodingResponse.mapData.longitude
      ) {
        let coordinates = {
          lat: geoCodingResponse.mapData.latitude,
          lng: geoCodingResponse.mapData.longitude,
        };
        setCoordinates([
          {
            location: {
              ...coordinates,
            },
            id: 1,
            label: undefined,
          },
        ]);
        setShowFindAddressAlert(false);
        setFindingAddress(true);
      }
    } else if (typeof geoCodingResponse === "string") {
      setShowFindAddressAlert(true);
      setFindingAddress(false);
    } else if (geoCodingResponse === null) {
      setGeoCodingError(true);
    }
  };

  const findAddressCheck = async (coordinates: MapData[] | null) => {
    if (findAddressRef.current > 0) {
      if (field.value && field.value.addressLine1) {
        const parsedAddress = createParsedAddress(field);
        const queryObj: searchParams = { address: parsedAddress };

        const geoCodingResponse = await handleAddressSearch(field, queryObj);

        handleGeoCodingResponse(geoCodingResponse);
      } else if (
        coordinates &&
        Array.isArray(coordinates) &&
        coordinates.length === 1 &&
        coordinates[0] &&
        typeof coordinates[0] === "object" &&
        coordinates[0].location &&
        coordinates[0].location.lat &&
        !isNaN(coordinates[0].location.lat) &&
        typeof coordinates[0].location.lat === "number" &&
        coordinates[0].location.lat >= -90 &&
        coordinates[0].location.lat <= 90 &&
        coordinates[0].location.lng &&
        !isNaN(coordinates[0].location.lng) &&
        typeof coordinates[0].location.lng === "number" &&
        coordinates[0].location.lng >= -180 &&
        coordinates[0].location.lng <= 180
      ) {
        const queryString = Object.values(coordinates[0].location).join(",");
        const queryObj: searchParams = { latlng: queryString };

        const geoCodingResponse = await handleAddressSearch(field, queryObj);

        handleGeoCodingResponse(geoCodingResponse);
      } else {
        setShowFindAddressAlert(true);
        setFindingAddress(false);
        setFieldValue("addressValid", true);
      }
      if (update) {
        setUpdate(null);
      }
    }
  };
  //parse found address
  useEffect(() => {
    if (findingAddress) {
      setParsedAddressSummary(createParsedAddress(field));
    }
  }, [findingAddress]);

  return (
    <div className={`FormBox FormSideBySide MapFieldRendererFormBox`}>
      <div className={`FormLabel`}>
        {/* <label>{label}</label> */}

        {tooltip && (
          <FormFieldTooltip
            tooltipText={tooltip}
            tooltipMainIconSource={tooltipMainIconSource}
            tooltipCloseIconSource={tooltipCloseIconSource}
          />
        )}
      </div>

      <div className={`FormField `}>
        <div
          className={`MapFieldRenderer ${relevantFormikError ? "Error" : ""}`}
        >
          {mapData ? (
            !findingAddress ? (
              <div className="MapData">
                <div className="Item">
                  <div className="Label">
                    Address line 1 <span className="Italic">- optional</span>
                  </div>

                  <input
                    type="text"
                    className={`Value ${
                      relevantFormikError &&
                      relevantFormikError["addressLine1"] &&
                      (submitCount > 0 || findAddressClickedCount > 0)
                        ? "Error"
                        : ""
                    }`}
                    name="addressLine1"
                    value={field.value?.addressLine1 ?? ""}
                    onChange={e => {
                      onChange("addressLine1", e.target.value);
                    }}
                    onBlur={() => handleOnBlur("addressLine1")}
                  />

                  {relevantFormikError &&
                    relevantFormikError["addressLine1"] &&
                    (submitCount > 0 || findAddressClickedCount > 0) && (
                      <div className="ErrorLabel">
                        {relevantFormikError["addressLine1"]}
                      </div>
                    )}
                </div>

                <div className="Item">
                  <div className="Label">
                    Address line 2 <span className="Italic">- optional</span>
                  </div>

                  <input
                    type="text"
                    className={`Value ${
                      relevantFormikError &&
                      relevantFormikError["addressLine2"] &&
                      (submitCount > 0 || findAddressClickedCount > 0)
                        ? "Error"
                        : ""
                    }`}
                    name="addressLine2"
                    value={field.value.addressLine2 ?? ""}
                    onChange={e => {
                      onChange("addressLine2", e.target.value);
                    }}
                    onBlur={() => handleOnBlur("addressLine2")}
                  />

                  {relevantFormikError &&
                    relevantFormikError["addressLine2"] &&
                    (submitCount > 0 || findAddressClickedCount > 0) && (
                      <div className="ErrorLabel">
                        {relevantFormikError["addressLine2"]}
                      </div>
                    )}
                </div>

                <div className="Item">
                  <div className="Label">
                    Address line 3 <span className="Italic">- optional</span>
                  </div>

                  <input
                    type="text"
                    className={`Value ${
                      relevantFormikError &&
                      relevantFormikError["addressLine3"] &&
                      (submitCount > 0 || findAddressClickedCount > 0)
                        ? "Error"
                        : ""
                    }`}
                    name="addressLine3"
                    value={field.value?.addressLine3 ?? ""}
                    onChange={e => {
                      onChange("addressLine3", e.target.value);
                    }}
                    onBlur={() => handleOnBlur("addressLine3")}
                  />

                  {relevantFormikError &&
                    relevantFormikError["addressLine3"] &&
                    (submitCount > 0 || findAddressClickedCount > 0) && (
                      <div className="ErrorLabel">
                        {relevantFormikError["addressLine3"]}
                      </div>
                    )}
                </div>

                <div className="Item">
                  <div className="Label">
                    Address line 4 <span className="Italic">- optional</span>
                  </div>

                  <input
                    type="text"
                    className={`Value ${
                      relevantFormikError &&
                      relevantFormikError["addressLine4"] &&
                      (submitCount > 0 || findAddressClickedCount > 0)
                        ? "Error"
                        : ""
                    }`}
                    name="addressLine4"
                    value={field.value.addressLine4 ?? ""}
                    onChange={e => {
                      onChange("addressLine4", e.target.value);
                    }}
                    onBlur={() => handleOnBlur("addressLine4")}
                  />

                  {relevantFormikError &&
                    relevantFormikError["addressLine4"] &&
                    (submitCount > 0 || findAddressClickedCount > 0) && (
                      <div className="ErrorLabel">
                        {relevantFormikError["addressLine4"]}
                      </div>
                    )}
                </div>

                <div className="TwoFieldRow">
                  <div className="Item">
                    <div className="Label">
                      City <span className="Italic">- optional</span>
                    </div>

                    <input
                      type="text"
                      className={`Value ${
                        relevantFormikError &&
                        relevantFormikError["city"] &&
                        (submitCount > 0 || findAddressClickedCount > 0)
                          ? "Error"
                          : ""
                      }`}
                      value={field.value.city ? field.value.city : ""}
                      name="city"
                      onChange={e => {
                        onChange("city", e.target.value);
                      }}
                      onBlur={() => handleOnBlur("city")}
                    />

                    {relevantFormikError &&
                      relevantFormikError["city"] &&
                      (submitCount > 0 || findAddressClickedCount > 0) && (
                        <div className="ErrorLabel">
                          {relevantFormikError["city"]}
                        </div>
                      )}
                  </div>

                  <div className="Item">
                    <div className="Label">
                      State/Province <span className="Italic">- optional</span>
                    </div>

                    {field.value["countriesId"] &&
                    countriesInfo &&
                    countriesInfo.hasOwnProperty(field.value["countriesId"]) &&
                    countriesInfo[field.value["countriesId"]] ===
                      "UNITED_STATES_OF_AMERICA" ? ( //ADD CODE WHEN KNOWN
                      usStates ? (
                        <Dropdown
                          value={selectedUsState}
                          options={usStates}
                          onChange={stateOnChange}
                          customNoOptionsMessage="No options"
                          onBlur={() => handleOnBlur("usStatesID")}
                        />
                      ) : (
                        <div className="Field NoData">No US State data</div>
                      )
                    ) : (
                      <>
                        <input
                          type="text"
                          className={`Value ${
                            relevantFormikError &&
                            relevantFormikError["stateProvince"] &&
                            (submitCount > 0 || findAddressClickedCount > 0)
                              ? "Error"
                              : ""
                          }`}
                          name="stateProvince"
                          value={
                            field.value.stateProvince
                              ? field.value.stateProvince
                              : ""
                          }
                          onChange={e => {
                            onChange("stateProvince", e.target.value);
                          }}
                          onBlur={() => handleOnBlur("stateProvince")}
                        />
                      </>
                    )}
                    {relevantFormikError &&
                      relevantFormikError["stateProvince"] &&
                      (submitCount > 0 || findAddressClickedCount > 0) && (
                        <div className="ErrorLabel">
                          {relevantFormikError["stateProvince"]}
                        </div>
                      )}
                  </div>
                </div>

                <div className="TwoFieldRow">
                  <div className="Item">
                    <div className="Label">
                      Postcode / Zip Code{" "}
                      <span className="Italic">- optional</span>
                    </div>

                    <input
                      type="text"
                      className={`Value ${
                        relevantFormikError &&
                        relevantFormikError["postalZipCode"] &&
                        (submitCount > 0 || findAddressClickedCount > 0)
                          ? "Error"
                          : ""
                      }`}
                      name="postalZipCode"
                      value={
                        field.value.postalZipCode
                          ? field.value.postalZipCode
                          : ""
                      }
                      onChange={e => {
                        onChange("postalZipCode", e.target.value);
                      }}
                      onBlur={() => handleOnBlur("postalZipCode")}
                    />

                    {relevantFormikError &&
                      relevantFormikError["postalZipCode"] &&
                      (submitCount > 0 || findAddressClickedCount > 0) && (
                        <div className="ErrorLabel">
                          {relevantFormikError["postalZipCode"]}
                        </div>
                      )}
                  </div>

                  <div className="Item">
                    <div className="Label">Country</div>

                    {countries ? (
                      <Dropdown
                        options={countries}
                        value={selectedCountry}
                        onChange={countryOnChange}
                        customClassName={`EditSingleSelect ${
                          relevantFormikError &&
                          relevantFormikError["countriesId"] &&
                          (submitCount > 0 || findAddressClickedCount > 0)
                            ? "Error"
                            : ""
                        }`}
                        customNoOptionsMessage="No options"
                        onBlur={() => handleOnBlur("countriesId")}
                      />
                    ) : (
                      <div className="Field NoData">No country data</div>
                    )}

                    {relevantFormikError &&
                      relevantFormikError["countriesId"] &&
                      (submitCount > 0 || findAddressClickedCount > 0) && (
                        <div className="ErrorLabel">
                          {relevantFormikError["countriesId"]}
                        </div>
                      )}
                  </div>
                </div>

                {!disableLongitudeAndLatitude && (
                  <div className="TwoFieldRow">
                    <div className="Item">
                      <div className="Label">
                        Latitude <span className="Italic">- optional</span>
                      </div>

                      <input
                        type="number"
                        className={`Value ${
                          relevantFormikError &&
                          relevantFormikError["latitude"] &&
                          (submitCount > 0 || findAddressClickedCount > 0)
                            ? "Error"
                            : ""
                        }`}
                        value={
                          field.value.latitude ? field.value.latitude : null
                        }
                        name="city"
                        onChange={e => {
                          onChange("latitude", e.target.value);
                        }}
                        onBlur={() => handleOnBlur("latitude")}
                      />

                      {relevantFormikError &&
                        relevantFormikError["latitude"] &&
                        (submitCount > 0 || findAddressClickedCount > 0) && (
                          <div className="ErrorLabel">
                            {relevantFormikError["latitude"]}
                          </div>
                        )}
                    </div>

                    <div className="Item">
                      <div className="Label">
                        Longitude <span className="Italic">- optional</span>
                      </div>

                      <input
                        type="number"
                        className={`Value ${
                          relevantFormikError &&
                          relevantFormikError["longitude"] &&
                          (submitCount > 0 || findAddressClickedCount > 0)
                            ? "Error"
                            : ""
                        }`}
                        name="stateProvince"
                        value={
                          field.value.longitude ? field.value.longitude : null
                        }
                        onChange={e => {
                          onChange("longitude", e.target.value);
                        }}
                        onBlur={() => handleOnBlur("longitude")}
                      />

                      {relevantFormikError &&
                        relevantFormikError["longitude"] &&
                        (submitCount > 0 || findAddressClickedCount > 0) && (
                          <div className="ErrorLabel">
                            {relevantFormikError["longitude"]}
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="AddressSummary">
                <div className="Label">Address:</div>
                <div className="Text">{parsedAddressSummary}</div>
              </div>
            )
          ) : (
            <div className="NoMapData">No map data</div>
          )}

          {!disableLongitudeAndLatitude && showFindAddressAlert && (
            <div className="ShowFindAddressAlert">
              <img src={errorIcon} alt="error icon" className="Icon" />
              {GeoCodingError ? (
                <div className="Text">
                  Oops something went wrong, please try again
                </div>
              ) : (
                <div className="Text">
                  No results found, please provide more information or valid
                  latitude and longitude values
                </div>
              )}
            </div>
          )}

          {!disableLongitudeAndLatitude && (
            <>
              {!findingAddress && (
                <div
                  className={`Btn FindAddress`}
                  onClick={() => {
                    findAddressRef.current++;
                    findAddressCheck(coordinates);
                  }}
                >
                  Find address
                </div>
              )}
            </>
          )}
          {editPanel && update && (
            <div className="Btn RelocateSite Cancel" onClick={cancelRelocate}>
              {`Cancel ${update === "RELOCATE" ? "Relocate" : "Update"}`}
            </div>
          )}
        </div>
      </div>
      {!disableLongitudeAndLatitude && (
        <div className="MapWrapper">
          {findingAddress && (
            <>
              {coordinates ? (
                <Map
                  data={coordinates}
                  handleMarkerSelected={() => {}}
                  selectedId={1}
                  onDrag={() => {}}
                  onZoomAnimationStart={() => {}}
                  withBounds
                />
              ) : (
                <div className="ShowFindAddressAlert">
                  <img src={errorIcon} alt="error icon" className="Icon" />
                  <div className="Text">
                    Please update address with more information to display Map
                  </div>
                </div>
              )}
              {!coordinates ? (
                <div className="Btn RelocateSite" onClick={editSite}>
                  Update address
                </div>
              ) : (
                <>
                  <div className="Btn FindAddress" onClick={editSite}>
                    Edit address
                  </div>
                  {editPanel && (
                    <div className="Btn RelocateSite" onClick={relocateSite}>
                      Relocate site
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

type MapReadOnlyProps = {
  name: string;
  dataSource: string | null;
  label: string;
  tooltip: string | null;
};

const MapReadOnly: React.FC<MapReadOnlyProps> = ({
  name,
  dataSource,
  label,
  tooltip,
}) => {
  const { values } = useFormikContext<SiteFields>();
  const [field] = useField<any>(name);
  const [parsedAddressSummary, setParsedAddressSummary] = useState<
    string | null
  >(null);
  const [address, setAddress] = useState<AddressMapData | null>(null);
  const [coordinates, setCoordinates] = useState<MapData[] | null>(null);

  useUpdateCoordinates(address, values.id);

  const findCoordinates = async (address: string) => {
    const queryObj: searchParams = { address: address };
    const geoCodingResponse = await handleAddressSearch(
      field,
      queryObj,
      field.value
    );

    if (
      geoCodingResponse &&
      typeof geoCodingResponse === "object" &&
      geoCodingResponse.hasOwnProperty("mapData") &&
      geoCodingResponse.mapData.hasOwnProperty("longitude") &&
      geoCodingResponse.mapData.hasOwnProperty("latitude") &&
      geoCodingResponse.mapData.latitude !== null &&
      typeof geoCodingResponse.mapData.latitude === "number" &&
      geoCodingResponse.mapData.longitude !== null &&
      typeof geoCodingResponse.mapData.longitude === "number"
    ) {
      setCoordinates([
        {
          location: {
            lat: geoCodingResponse.mapData.latitude,
            lng: geoCodingResponse.mapData.longitude,
          },
          label: undefined,
          id: 1,
        },
      ]);
      setAddress(geoCodingResponse.mapData);
    }
  };

  useEffect(() => {
    setParsedAddressSummary(createParsedAddress(field));
  }, []);

  useEffect(() => {
    {
      if (
        field.value &&
        field.value.hasOwnProperty("latitude") &&
        field.value["latitude" as keyof typeof field.value] !== null &&
        typeof field.value["latitude" as keyof typeof field.value] ===
          "number" &&
        field.value.hasOwnProperty("longitude") &&
        field.value["longitude" as keyof typeof field.value] !== null &&
        typeof field.value["longitude" as keyof typeof field.value] === "number"
      ) {
        setCoordinates([
          {
            location: {
              lat: field.value["latitude" as keyof typeof field.value],
              lng: field.value["longitude" as keyof typeof field.value],
            },
            label: undefined,
            id: 1,
          },
        ]);
      } else {
        if (parsedAddressSummary) {
          findCoordinates(parsedAddressSummary);
        } else {
          setCoordinates(null);
        }
      }
    }
  }, [field.value, parsedAddressSummary]);

  return (
    <div key={`${name}`} className="FormBox FormSideBySide">
      <div className="FormField">
        <div className="EditBox ViewOnly MapRenderer">
          {parsedAddressSummary ? (
            <table className="ViewTable">
              <tr>
                <th style={{ paddingRight: 20 }}>Address</th>
                <th>{parsedAddressSummary}</th>
              </tr>
            </table>
          ) : (
            <div className="SitesViewNoData">{`No data`}</div>
          )}

          {coordinates ? (
            <>
              <Map
                data={coordinates}
                handleMarkerSelected={() => {}}
                selectedId={1}
                onDrag={() => {}}
                onZoomAnimationStart={() => {}}
                withBounds
                view
              />
            </>
          ) : (
            <div className="ShowFindAddressAlert ReadOnly">
              <img src={errorIcon} alt="error icon" className="Icon" />
              <div className="Text">
                Please update address with more information to display Map
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const mapFieldRenderer = (
  readOnly: boolean,
  disableLongitudeAndLatitude: boolean = false
) =>
  function fileUploadFieldRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    if (readOnly) {
      return (
        <MapReadOnly
          name={String(field.name)}
          dataSource={
            field.dataSource && typeof field.dataSource === "string"
              ? pascalCaseToCamelCase(field.dataSource)
              : null
          }
          label={field.label}
          tooltip={field.toolTip ? field.toolTip : null}
        />
      );
    } else {
      return (
        <MapCreateEdit
          name={String(field.name)}
          dataSource={
            field.dataSource && typeof field.dataSource === "string"
              ? pascalCaseToCamelCase(field.dataSource)
              : null
          }
          label={field.label}
          tooltip={field.toolTip ? field.toolTip : null}
          disableLongitudeAndLatitude={disableLongitudeAndLatitude}
        />
      );
    }
  };

export default mapFieldRenderer;

export const createMapData = (): MapData[] => {
  const testCoOrdinates = [51.52202580581433, -0.12717753458911493];
  const testCoOrdinates2 = [51.53836942063738, -0.1537378306523003];
  const formattedData: {
    id: number;
    location: { latitude: number; longitude: number } | undefined;
  }[] = [
    {
      id: 1,
      location: { latitude: testCoOrdinates[0], longitude: testCoOrdinates[1] },
    },
    {
      id: 2,
      location: {
        latitude: testCoOrdinates2[0],
        longitude: testCoOrdinates2[1],
      },
    },
  ];

  // @ts-ignore
  return formattedData
    .filter(a => a.location)
    .map((item, idx) => ({
      id: item.id,
      label: `Map ${idx}`,
      location: {
        lat: +item.location!.latitude,
        lng: +item.location!.longitude,
      },
    }));
};
